import React, { useContext, useEffect, useState } from "react";
import { ConfiguracoesContext } from "../../../contexts/Configuracoes/ConfiguracoesContext";
import Titulo from "../../Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import { Toggle } from "../../Toggle/Toggle";
import { copy } from "../../../services/Functions";
import { ContainerFreteEspecialListagem } from "./styles";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { currencyMask } from "../../../services/Masks";
import Table from "../../Table/Table";
import { Select } from "../../Select/index";
import Paginacao from "../../Paginacao";
import { Input } from "../../Input";

interface FreteEspecialListagemProps {
  onEditarFrete: (index: number) => void;
  onNovoFrete?: () => void;
  /** ação exibida no centro do rodapé, na mesma linha da paginação */
  botaoAdicionar?: React.ReactNode;
  configIn: "empresa" | "configuracoes";
}

function formatarCep(cep: string | number) {
  return cep.toString().replace(/(\d{5})(\d{3})/, "$1-$2");
}

function normalizarBusca(texto: string) {
  return texto.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

const FreteEspecialListagem: React.FC<FreteEspecialListagemProps> = ({
  onEditarFrete,
  onNovoFrete,
  configIn,
  botaoAdicionar,
}) => {
  const { theme } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [itensPorPagina] = useState(20);
  const [filtroAtivo, setFiltroAtivo] = useState<'todos' | 'ativos' | 'inativos'>('ativos');
  const [busca, setBusca] = useState("");
  const [buscaDigitada, setBuscaDigitada] = useState("");

  const { fretes_especiais, setFretes_especiais } =
    useContext(ConfiguracoesContext);

  const isFreteAtivo = (ativo: any): boolean => {
    return ativo === true || ativo === 1 || ativo === "1" || ativo === "true";
  };

  const fretesFiltrados = fretes_especiais.filter(frete => {
    if (filtroAtivo === 'ativos') {
      return isFreteAtivo(frete.ativo);
    } else if (filtroAtivo === 'inativos') {
      return !isFreteAtivo(frete.ativo);
    }
    return true; 
  }).filter(frete => {
    const termo = normalizarBusca(busca);
    if (!termo) return true;

    if (normalizarBusca(frete.descricao || "").includes(termo)) return true;

    // CEP: casa com parte do CEP inicial/final ou, com o CEP completo, com a faixa que o contém
    const digitos = busca.replace(/\D/g, "");
    if (!digitos) return false;
    const cepInicio = (frete.cep_inicio || "").toString().replace(/\D/g, "");
    const cepFim = (frete.cep_fim || "").toString().replace(/\D/g, "");
    if (cepInicio.includes(digitos) || cepFim.includes(digitos)) return true;
    return (
      digitos.length === 8 &&
      cepInicio.length > 0 &&
      cepFim.length > 0 &&
      Number(digitos) >= Number(cepInicio) &&
      Number(digitos) <= Number(cepFim)
    );
  });

  // ordena por descrição antes de paginar, para que a ordem seja a mesma em todas as páginas
  const fretesOrdenados = [...fretesFiltrados].sort((a, b) =>
    (a.descricao || "").toString().localeCompare((b.descricao || "").toString(), undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );

  const totalPaginas = Math.max(1, Math.ceil(fretesOrdenados.length / itensPorPagina));

  useEffect(() => {
    setCurrentPage(1);
  }, [filtroAtivo, busca]);

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(totalPaginas);
  }, [currentPage, totalPaginas]);

  const tableData = fretesOrdenados.map((frete) => {
    const originalIndex = fretes_especiais.findIndex(f => f === frete);
    return {
    ...frete,
    cepRange: frete.cep_inicio && frete.cep_fim 
      ? `${formatarCep(frete.cep_inicio)} - ${formatarCep(frete.cep_fim)}`
      : "Não informado",
    prazoFormatado: frete.prazo 
      ? `${frete.prazo} dias`
      : "Não informado",
    valorFormatado: frete.valor 
      ? currencyMask((Number(frete.valor)).toString())
      : "Não informado",
    acoes: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Toggle
          value={frete.ativo}
          onChange={(newValue) => {
            const copia = copy(fretes_especiais);
            copia[originalIndex].ativo = newValue;
            setFretes_especiais(copia);
          }}
        />
        <span
          onClick={() => onEditarFrete(originalIndex)}
          className={`editar fonteTexto ${!frete.ativo ? "disabled" : ""}`}
          style={{ cursor: 'pointer' }}
        >
          Editar
        </span>
      </div>
    ),
    _originalIndex: originalIndex
    };
  });

  const dadosPagina = tableData.slice(
    (currentPage - 1) * itensPorPagina,
    currentPage * itensPorPagina
  );

  const tableColumns = [
    { 
      key: "descricao", 
      label: "Descrição",
      mask: (value: string) => {
        return (
          <span 
            onClick={() => {
              const matchingItem = dadosPagina.find(row => row.descricao === value);
              if (matchingItem) {
                onEditarFrete(matchingItem._originalIndex);
              }
            }}
            style={{ 
              cursor: 'pointer', 
            }}
            className="fonteTexto"
          >
            {value}
          </span>
        );
      }
    },
    { 
      key: "cepRange", 
      label: "CEP",
      mask: (value: string) => {
        return (
          <span 
            onClick={() => {
              const matchingItem = dadosPagina.find(row => row.cepRange === value);
              if (matchingItem) {
                onEditarFrete(matchingItem._originalIndex);
              }
            }}
            style={{ 
              cursor: 'pointer', 
            }}
            className="fonteTexto"
          >
            {value}
          </span>
        );
      }
    },
    { 
      key: "prazoFormatado", 
      label: "Prazo",
      mask: (value: string) => {
        return (
          <span 
            onClick={() => {
              // Busca o item correspondente na página atual
              const matchingItem = dadosPagina.find(row => row.prazoFormatado === value);
              if (matchingItem) {
                onEditarFrete(matchingItem._originalIndex);
              }
            }}
            style={{ 
              cursor: 'pointer', 
            }}
            className="fonteTexto"
          >
            {value}
          </span>
        );
      }
    },
    { 
      key: "valorFormatado", 
      label: "Valor",
      mask: (value: string) => {
        return (
          <span 
            onClick={() => {
              // Busca o item correspondente na página atual
              const matchingItem = dadosPagina.find(row => row.valorFormatado === value);
              if (matchingItem) {
                onEditarFrete(matchingItem._originalIndex);
              }
            }}
            style={{ 
              cursor: 'pointer', 
            }}
            className="fonteTexto"
          >
            {value}
          </span>
        );
      }
    },
    { 
      key: "acoes", 
      label: "Ações",
      mask: (value: any) => value
    }
  ];

  return (
    <>
      <ContainerFreteEspecialListagem configIn={configIn} theme={theme}>
        <div className="containerFreteEspecialListagem">
          {configIn === "empresa" && (
            <Titulo
              titulo="Entrega Especial"
              subtitulo={""}
              flexDirection="column-reverse"
            />
          )}
          <div className="textoFreteEspecial fonteTexto">
            Crie entregas personalizadas, como motoboy ou entrega combinada. <br />
            Defina prazo e valor por faixa de CEP para oferecer uma experiência sob medida e diferenciada aos seus clientes.
          </div>
          <div className="linha"></div>

          {fretes_especiais.length > 0 && (
            <div className="filtros-frete">
              <div className="busca-fretes">
                <Input
                  type="text"
                  icone="icons/search.svg"
                  placeholder="Pesquisar por descrição ou CEP"
                  value={buscaDigitada}
                  onChange={(newValue) => setBuscaDigitada(newValue)}
                  onDebounce={setBusca}
                  debounceTime={1000}
                  onKeyDown={(event) => {
                    // a listagem fica dentro do form de configurações; Enter não deve salvar
                    if (event.key === "Enter") event.preventDefault();
                  }}
                />
              </div>
              <Select
                value={filtroAtivo}
                onChange={(newValue) => setFiltroAtivo(newValue as 'todos' | 'ativos' | 'inativos')}
                options={[
                  { name: "Ativos", value: "ativos" },
                  { name: "Inativos", value: "inativos" },
                  { name: "Todos", value: "todos" }
                ]}
                className="select-filtro-fretes"
              />
            </div>
          )}
          <div className="fretesFlexContainer">
            {tableData.length > 0 ? (
              <Table
                columns={tableColumns}
                data={dadosPagina}
                sortable={true}
                selectable={false}
                button={false}
                page={1}
                itensPorPagina={itensPorPagina}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                {fretes_especiais.length === 0 
                  ? 'Nenhuma entrega especial cadastrada' 
                  : busca.trim()
                    ? 'Nenhuma entrega especial encontrada para a pesquisa'
                    : `Nenhuma entrega especial ${filtroAtivo === 'ativos' ? 'ativa' : 'inativa'} encontrada`
                }
              </div>
            )}
          </div>
          {(totalPaginas > 1 || botaoAdicionar) && (
            <div className="rodapeFretes">
              <div className="paginacaoFretes">
                {totalPaginas > 1 && (
                  <Paginacao
                    currentPage={currentPage}
                    totalPages={totalPaginas}
                    onPageChange={setCurrentPage}
                  />
                )}
              </div>
              <div className="adicionarFretes">{botaoAdicionar}</div>
              <div></div>
            </div>
          )}
          {configIn === 'empresa' && (
            <div
              className="novo"
              onClick={() => {
                onNovoFrete && onNovoFrete();
              }}
            >
              Configure uma nova entrega especial
            </div>
          )}
        </div>
      </ContainerFreteEspecialListagem>
    </>
  );
};
export default FreteEspecialListagem;
